// The machine: cabinet, dot-matrix display, reel grid with peek rows, lever, hold/nudge controls.
// It renders static state from the store and exposes animation hooks for the EventPlayer.
import { Container, Graphics, Sprite, type Ticker } from 'pixi.js';
import { B } from '../../content/balance';
import { PALETTE, type PaletteKey } from '../../art/palette';
import { getAtlas, textTexture } from '../../art/rasterize';
import { formatNumber } from '../../i18n/format';
import { t } from '../../i18n/t';
import { columnDefs, passiveReel } from '../../sim/grid';
import { computeModifiers } from '../../sim/modifiers';
import { previewNudge } from '../../sim/spin';
import type { Cell, FormId, GameState, PatternId, SizeId } from '../../sim/types';
import { dispatch, setUI, store } from '../../state/store';
import { toLogical, type GameApp } from '../app';
import { Arcade } from './Arcade';
import { Cabinet, type CabinetLayout } from './Cabinet';
import { Display } from './Display';
import { Lever } from './Lever';
import { PixelButton } from './PixelButton';
import { Reel, TickerHub } from './Reel';

export const PATTERN_COLOR: Record<PatternId, PaletteKey> = {
  pair: 'mint',
  line3: 'gold',
  line4: 'amber',
  line5: 'orange',
  col3: 'blue',
  diag3: 'lavender',
  vee: 'flesh',
  zig: 'lime',
  eye: 'crimson',
  cross: 'white',
  gorge: 'red',
  bandit: 'red',
};

export interface MachineLayout extends CabinetLayout {
  zoom: number;
  cell: number;
  gap: number;
  reelsX: number;
  reelsY: number;
  displayCols: number;
  displayRows: number;
  holdY: number;
  nudgeY: number;
  leverX: number;
  leverY: number;
  /** LED pitch of the display. */
  pitch: number;
  /** 5×4 and 5×5: the camera has pulled back (docs/07 §1.3) — small display, one peek row. */
  compact: boolean;
}

/** Peek rows the layout can show (the sim may reveal more; the reels trim them). */
export function visiblePeek(size: SizeId, peek: number): number {
  return B.SIZES[size].rows >= 4 ? Math.min(peek, 1) : peek;
}

const CENTER_X = 300;
/** A swipe down the reel window at least this long (logical px), this fast (ms), pulls the lever. */
const SWIPE_PX = 30;
const SWIPE_MS = 450;

export function machineLayout(size: SizeId, peekRows: number): MachineLayout {
  const { reels, rows } = B.SIZES[size];
  const compact = rows >= 4;
  const peek = visiblePeek(size, peekRows);
  const pitch = compact ? 2 : 3;
  const zoom = size === 'bandit' ? 2 : 1;
  const cell = 40 * zoom;
  const gap = zoom === 2 ? 4 : 3;
  const peekPx = peek > 0 ? Math.round(cell * (peek - 1) + cell * (zoom === 2 ? 0.3 : 0.4)) : 0;
  const reelsW = reels * cell + (reels - 1) * gap;
  const face = size !== 'bandit';
  const displayCols = 64;
  const displayRows = face ? 24 : 11;
  const dispW = displayCols * pitch;
  const dispH = displayRows * pitch;
  const bodyW = Math.max(dispW, reelsW + 8) + 32;
  const top = compact ? 18 : face ? 30 : 34;
  const display = { x: CENTER_X - Math.floor(dispW / 2), y: top + (compact ? 5 : 10), w: dispW, h: dispH };
  const windowTop = display.y + dispH + (compact ? 8 : 18);
  const reelsY = windowTop + peekPx;
  const window = { x: CENTER_X - Math.floor(reelsW / 2), y: windowTop, w: reelsW, h: rows * cell + peekPx * 2 };
  const holdY = window.y + window.h + (compact ? 3 : 7);
  const nudgeY = holdY + (compact ? 14 : 16);
  const tray = compact ? { x: CENTER_X - 30, y: nudgeY + 13, w: 60, h: 0 } : { x: CENTER_X - 30, y: nudgeY + 18, w: 60, h: 10 };
  const body = { x: CENTER_X - Math.floor(bodyW / 2), y: top, w: bodyW, h: tray.y + tray.h + (compact ? 3 : 8) - top };
  return {
    zoom,
    cell,
    gap,
    reelsX: window.x,
    reelsY,
    displayCols,
    displayRows,
    holdY,
    nudgeY,
    body,
    display,
    window,
    tray,
    leverX: body.x + body.w + 22,
    leverY: body.y + Math.floor(body.h * 0.45),
    pitch,
    compact,
  };
}

export class MachineScene extends Container {
  readonly arcade = new Arcade();
  readonly cabinet = new Cabinet();
  display!: Display;
  reels: Reel[] = [];
  lever!: Lever;
  private holdButtons: PixelButton[] = [];
  private nudgeButtons: { up: PixelButton; down: PixelButton }[] = [];
  readonly lines = new Graphics();
  readonly overReels = new Container();
  private preview = new Sprite();
  /** Touch: swipe down on the reels to pull (docs/07 §1.3). */
  private swipe = new Graphics();
  private swipeStart: { y: number; t: number } | null = null;
  /** Gamepad: marks the reel the D-pad picked. */
  private padCursor = new Graphics();
  private reelLayer = new Container();
  private controls = new Container();
  size: SizeId | null = null;
  peek = 1;
  layout!: MachineLayout;
  animating = false;
  onPullRequest: (perfect: boolean, fromLever: boolean) => void = () => {};

  constructor(private ga: GameApp) {
    super();
    this.addChild(this.arcade, this.cabinet, this.reelLayer, this.cabinet.glass, this.lines, this.overReels, this.swipe, this.padCursor, this.controls, this.preview);
    this.swipe.eventMode = 'static';
    this.swipe.on('pointerdown', (e) => (this.swipeStart = { y: e.global.y, t: performance.now() }));
    this.swipe.on('pointerup', (e) => {
      const s = this.swipeStart;
      this.swipeStart = null;
      if (s && e.global.y - s.y > SWIPE_PX && performance.now() - s.t < SWIPE_MS) this.onPullRequest(false, false);
    });
    this.swipe.on('pointerupoutside', () => (this.swipeStart = null));
    this.preview.anchor.set(0.5, 1);
    TickerHub.ticker = ga.app.ticker;
    ga.app.ticker.add(this.tick);
    ga.app.canvas.addEventListener('pointermove', (e) => {
      if (!this.display) return;
      const p = toLogical(ga, e.clientX, e.clientY);
      const d = this.layout.display;
      this.display.look((p.x - (d.x + d.w / 2)) / (d.w / 2), (p.y - (d.y + d.h / 2)) / (d.h / 2));
    });
  }

  private tick = (t: Ticker) => {
    this.display?.update(t);
    this.cabinet.tick(t.deltaMS, this.animating ? 3 : 1);
    const g = store.getState().game;
    this.lever?.wiggle(t.deltaMS, !!g.run && g.run.phase === 'ready' && g.run.stats.spins === 0 && !this.animating);
  };

  build(size: SizeId, peekRows: number, form: FormId = this.cabinet.form): void {
    if (this.size === size && this.peek === peekRows && this.cabinet.form === form) return;
    this.size = size;
    this.peek = peekRows;
    const l = (this.layout = machineLayout(size, peekRows));
    const peek = visiblePeek(size, peekRows);
    this.cabinet.build(l, form);
    for (const r of this.reels) r.destroy();
    this.reels = [];
    this.reelLayer.removeChildren();
    const { reels, rows } = B.SIZES[size];
    for (let i = 0; i < reels; i++) {
      const r = new Reel({ index: i, cell: l.cell, zoom: l.zoom, rows, peek });
      r.position.set(l.reelsX + i * (l.cell + l.gap), l.reelsY);
      this.reels.push(r);
      this.reelLayer.addChild(r);
    }
    const prevDisplay = this.display;
    if (prevDisplay) {
      this.removeChild(prevDisplay);
      prevDisplay.destroy({ children: true });
    }
    this.display = new Display(l.displayCols, l.displayRows, l.pitch);
    this.display.position.set(l.display.x, l.display.y);
    this.display.hasFace = size !== 'bandit';
    this.addChildAt(this.display, 2);
    if (this.display.hasFace) this.display.showFace('neutral');
    else this.display.marquee(t('ui.title'));

    if (!this.lever) {
      this.lever = new Lever();
      this.lever.onPull = (perfect) => this.onPullRequest(perfect, true);
      this.addChild(this.lever);
    }
    this.lever.position.set(l.leverX, l.leverY);
    this.swipe.clear().rect(l.window.x, l.window.y, l.window.w, l.window.h).fill({ color: PALETTE.ink, alpha: 0.001 });
    this.buildControls(reels);
  }

  private buildControls(reels: number): void {
    const l = this.layout;
    this.controls.removeChildren().forEach((c) => c.destroy());
    this.holdButtons = [];
    this.nudgeButtons = [];
    const bw = Math.min(l.cell, 44);
    for (let i = 0; i < reels; i++) {
      const cx = l.reelsX + i * (l.cell + l.gap) + l.cell / 2;
      const hold = new PixelButton(bw, 13, t('ui.hold'));
      hold.position.set(Math.round(cx - bw / 2), l.holdY);
      hold.onClick = () => dispatch({ t: 'TOGGLE_HOLD', reel: i });
      this.holdButtons.push(hold);
      const a = getAtlas();
      const up = new PixelButton(18, 13, null, a.tex('arrow_up'));
      const down = new PixelButton(18, 13, null, a.tex('arrow_down'));
      up.position.set(Math.round(cx - 19), l.nudgeY);
      down.position.set(Math.round(cx + 1), l.nudgeY);
      // ▲ moves the reel up (the row below slides in); ▼ moves it down.
      up.onClick = () => dispatch({ t: 'NUDGE', reel: i, dir: -1 });
      down.onClick = () => dispatch({ t: 'NUDGE', reel: i, dir: 1 });
      up.onHover = (on) => setUI({ nudgeHover: on ? { reel: i, dir: -1 } : null });
      down.onHover = (on) => setUI({ nudgeHover: on ? { reel: i, dir: 1 } : null });
      this.nudgeButtons.push({ up, down });
      this.controls.addChild(hold, up, down);
    }
  }

  /** Static sync from the true state (when nothing is animating). */
  sync(game: GameState): void {
    const run = game.run;
    if (!run) return;
    const mods = computeModifiers(game.meta, run);
    this.build(run.size, mods.peekRows, game.meta.form);
    this.arcade.sync(game, mods.features.has('eyes'));
    const pool = [...new Set(run.deck.map((d) => d.defId))];
    const busy = store.getState().ui.busy;
    const nightWild = run.nightShift && run.nightShift.left > 0 ? run.nightShift.wild : -1;
    this.reels.forEach((r, i) => {
      r.setPool(pool);
      if (!busy && !this.animating && run.strips[i]?.length) r.setColumn(columnDefs(run, i, run.stops[i] ?? 0, mods.peekRows, run.stops));
      r.setHeld(!!run.held[i]);
      r.setWild(i === nightWild);
      r.setLocked(mods.lockedReels.has(i) || passiveReel(run, i));
    });
    const ready = run.phase === 'ready' || run.phase === 'nudge';
    const canHold = mods.features.has('hold');
    const canNudge = mods.features.has('nudge');
    const heldCount = run.held.filter(Boolean).length;
    const ui = store.getState().ui;
    this.padCursor.clear();
    if (ui.padActive && this.reels[ui.padReel]) {
      const r = this.reels[ui.padReel]!;
      this.padCursor.rect(r.x - 2, this.layout.window.y - 3, r.cellSize + 4, 2).fill(PALETTE.mint);
      this.padCursor.rect(r.x - 2, this.layout.window.y + this.layout.window.h + 1, r.cellSize + 4, 2).fill(PALETTE.mint);
    }
    this.holdButtons.forEach((b, i) => {
      b.visible = canHold;
      const held = !!run.held[i];
      const locked = mods.lockedReels.has(i) || passiveReel(run, i);
      b.set(ready && !busy && (held || (!locked && run.charges.hold > 0 && heldCount + 1 < run.held.length)), held);
      b.setText(held ? t('ui.held') : t('ui.hold'));
    });
    const nudging = run.phase === 'nudge' && !busy;
    this.nudgeButtons.forEach(({ up, down }, i) => {
      up.visible = down.visible = canNudge;
      const ok = nudging && run.charges.nudge > 0 && !mods.lockedReels.has(i) && !passiveReel(run, i);
      up.set(ok);
      down.set(ok);
    });
    this.lever.enabled = run.phase === 'ready' || run.phase === 'nudge';
    this.lever.setSkin(!!game.meta.flags.cosmetic_maw);
    this.updatePreview(game);
  }

  private updatePreview(game: GameState): void {
    const hover = store.getState().ui.nudgeHover;
    const run = game.run;
    const mods = computeModifiers(game.meta, run);
    if (!hover || !run || run.phase !== 'nudge' || !run.spin || mods.hidePreview) {
      this.preview.visible = false;
      return;
    }
    const ev = previewNudge(game, mods, hover.reel, hover.dir);
    if (!ev) return;
    const delta = ev.payout.sub(run.spin.payout);
    const sign = delta.gte(0) ? '+' : '-';
    const text = `${sign}${formatNumber(delta.abs(), game.meta.settings.notation)}`;
    this.preview.texture = textTexture(text, delta.gt(0) ? 'clover' : delta.lt(0) ? 'red' : 'paper', 'ink');
    const r = this.reels[hover.reel]!;
    this.preview.position.set(Math.round(r.x + r.cellSize / 2), this.layout.window.y - 4);
    this.preview.visible = true;
  }

  cellCenter(c: Cell): { x: number; y: number } {
    const r = this.reels[c.reel]!;
    return r.cellCenter(c.row);
  }

  drawPattern(cells: Cell[], color: PaletteKey): void {
    if (cells.length === 0) return;
    const w = this.layout.zoom === 2 ? 3 : 2;
    const pts = cells.map((c) => this.cellCenter(c));
    const g = this.lines;
    const col = PALETTE[color];
    if (pts.length === 1 || cells.length > 5) {
      for (const c of cells) {
        const r = this.reels[c.reel]!;
        g.rect(r.x + 1, r.y + c.row * r.cellSize + 1, r.cellSize - 2, r.cellSize - 2).stroke({ color: col, width: w, alignment: 1 });
      }
      return;
    }
    // line with an ink outline for readability
    for (const [width, c] of [
      [w + 2, PALETTE.ink],
      [w, col],
    ] as const) {
      g.moveTo(pts[0]!.x, pts[0]!.y);
      for (const p of pts.slice(1)) g.lineTo(p.x, p.y);
      g.stroke({ color: c, width, cap: 'square', join: 'miter' });
    }
    for (const p of pts) g.rect(p.x - w, p.y - w, w * 2, w * 2).fill(col);
  }

  clearPatterns(): void {
    this.lines.clear();
  }

  reelTop(reel: number): { x: number; y: number } {
    const r = this.reels[reel]!;
    return { x: r.x + r.cellSize / 2, y: this.layout.window.y };
  }

  gridCenter(): { x: number; y: number } {
    const w = this.layout.window;
    return { x: w.x + w.w / 2, y: w.y + w.h / 2 };
  }
}
